// File: frontend/src/pages/Contact.js
//
// Public Contact page at /contact.
//
// Two paths: someone new to PesaFii, and an operator who already uses it. Both
// reach the same approved support line (config/publicSupport.js) by WhatsApp
// or phone. The prefilled WhatsApp text differs by path, so support can tell a
// prospect from a customer.
//
// What is deliberately NOT here:
// - a form: there is no enquiry backend, and a form that goes nowhere is worse
//   than none;
// - an email address: there is no general support mailbox (legal@ and privacy@
//   exist for their own purposes and live in the legal documents);
// - opening hours, response times or a street address: none are published;
// - a "support centre" link: the in-app /support page is a placeholder.

import React from "react";
import { Box, Button, Container, Link, Paper, Stack, Typography, alpha } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PublicLayout from "../components/marketing/PublicLayout";
import StructuredData, { organizationLd } from "../components/marketing/StructuredData";
import {
  SUPPORT_PHONE_DISPLAY, SUPPORT_TEL_HREF, WHATSAPP_MESSAGES, whatsappUrl,
} from "../config/publicSupport";
import { WHATSAPP } from "../theme";

const TOPICS = ["Getting started", "Setting up your hotspot", "Payments and withdrawals", "Product questions", "Help with your account"];

const whatsappButtonSx = (t) => {
  const wa = t.palette.whatsapp || WHATSAPP;
  return { bgcolor: wa.main, color: wa.contrastText, "&:hover": { bgcolor: wa.hover } };
};

function ContactPath({ id, eyebrow, title, text, whatsappLabel, whatsappMessage, extra }) {
  return (
    <Paper
      component="section"
      aria-labelledby={id}
      sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, border: "1px solid", borderColor: "divider", minWidth: 0, display: "flex", flexDirection: "column" }}
    >
      <Typography variant="overline" component="p" sx={{ color: "primary.main" }}>{eyebrow}</Typography>
      <Typography id={id} variant="h3" component="h2" sx={{ mt: 0.5, color: "text.primary" }}>{title}</Typography>
      <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", flex: 1 }}>{text}</Typography>
      <Stack spacing={1.25} sx={{ mt: 3 }}>
        <Button
          component="a"
          href={whatsappUrl(whatsappMessage)}
          target="_blank"
          rel="noopener noreferrer"
          size="large"
          startIcon={<WhatsAppIcon aria-hidden="true" />}
          sx={(t) => ({ ...whatsappButtonSx(t), minHeight: 48, borderRadius: 2, textTransform: "none", fontWeight: 700 })}
        >
          {whatsappLabel}
        </Button>
        <Button
          component="a"
          href={SUPPORT_TEL_HREF}
          variant="outlined"
          size="large"
          startIcon={<PhoneOutlinedIcon aria-hidden="true" />}
          sx={{ minHeight: 48, borderRadius: 2, textTransform: "none", fontWeight: 700, color: "text.primary", borderColor: "divider" }}
        >
          Call {SUPPORT_PHONE_DISPLAY}
        </Button>
        {extra}
      </Stack>
    </Paper>
  );
}

export default function Contact() {
  return (
    <PublicLayout
      title="Contact PesaFii | WhatsApp and Phone Support"
      description={`Contact PesaFii about getting started, hotspot setup, payments or your account. Chat on WhatsApp or call ${SUPPORT_PHONE_DISPLAY}.`}
    >
      <StructuredData data={organizationLd()} />
      <Box
        sx={(t) => ({
          pt: { xs: 5, md: 8 },
          pb: { xs: 3, md: 5 },
          background: `radial-gradient(700px 260px at 50% -40px, ${alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.12 : 0.08)}, transparent 70%)`,
        })}
      >
        <Container maxWidth="md" sx={{ textAlign: "center", minWidth: 0 }}>
          <Typography component="h1" variant="h1" sx={{ fontSize: "clamp(2rem, 5vw, 2.75rem)", color: "text.primary" }}>
            Contact PesaFii
          </Typography>
          <Typography variant="body1" sx={{ mt: 1.5, color: "text.secondary", maxWidth: 560, mx: "auto" }}>
            Talk to us about getting started, setting up your hotspot, payments and withdrawals, or anything
            about your PesaFii account. WhatsApp is the quickest way to reach us.
          </Typography>
          <Stack component="ul" direction="row" useFlexGap flexWrap="wrap" justifyContent="center" columnGap={1} rowGap={1} sx={{ listStyle: "none", m: 0, p: 0, mt: 3 }}>
            {TOPICS.map((topic) => (
              <Typography
                key={topic}
                component="li"
                variant="subtitle2"
                sx={{ px: 1.5, py: 0.5, borderRadius: 99, border: "1px solid", borderColor: "divider", color: "text.secondary", bgcolor: "background.paper" }}
              >
                {topic}
              </Typography>
            ))}
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pb: { xs: 6, md: 9 }, minWidth: 0 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { xs: 2, md: 3 } }}>
          <ContactPath
            id="contact-new"
            eyebrow="New to PesaFii"
            title="Start selling Wi-Fi"
            text="Ask how PesaFii works for your hotspot, which router you need, or what it costs. Registration is free."
            whatsappLabel="Chat on WhatsApp"
            whatsappMessage={WHATSAPP_MESSAGES.general}
            extra={(
              <Button component={RouterLink} to="/login?mode=register" size="large" sx={{ minHeight: 48, borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
                Start Free
              </Button>
            )}
          />
          <ContactPath
            id="contact-customer"
            eyebrow="Already using PesaFii"
            title="Get help with your account"
            text="For help with routers, packages, vouchers, payments or withdrawals, message or call us with your business name."
            whatsappLabel="WhatsApp Support"
            whatsappMessage={WHATSAPP_MESSAGES.customer}
            extra={(
              <Button component={RouterLink} to="/login?mode=login" size="large" sx={{ minHeight: 48, borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
                Sign In
              </Button>
            )}
          />
        </Box>

        <Paper sx={{ mt: { xs: 3, md: 4 }, p: { xs: 3, md: 4 }, borderRadius: 3, border: "1px solid", borderColor: "divider", bgcolor: "action.hover" }}>
          <Typography variant="h3" component="h2" sx={{ color: "text.primary" }}>Company</Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            PesaFii is developed and operated by Agrinal Enterprises Limited, Buwambo Kiti, Uganda.
          </Typography>
          <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1} sx={{ mt: 2 }}>
            <Link component={RouterLink} to="/#faq" sx={{ fontWeight: 600, display: "inline-flex", alignItems: "center", minHeight: 44 }}>Frequently asked questions</Link>
            <Link component={RouterLink} to="/privacy" sx={{ fontWeight: 600, display: "inline-flex", alignItems: "center", minHeight: 44 }}>Privacy Notice</Link>
            <Link component={RouterLink} to="/terms" sx={{ fontWeight: 600, display: "inline-flex", alignItems: "center", minHeight: 44 }}>Terms of Service</Link>
          </Stack>
        </Paper>
      </Container>
    </PublicLayout>
  );
}
